/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { Params } from '@angular/router';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { NavOption } from '@src/app/model/sys/navOption';
import { ITabField } from '@src/app/shared/field/TabFIeld';
import { ChartAnalysis } from '@src/app/shared/chart/chart-analysis';
import { FormMode, IFormAction } from '@src/app/shared/form/form.component';
import { HttpParams } from '@angular/common/http';
import { NavRoute } from '@src/app/shared/NavRoute';
import { Field } from '@src/app/shared/field/Field';

export class FormConfig {
    fieldSet: FieldSet;
    service?: AbstractHttpService;
    title: string;
    icon?: string;
    forTeamName?: string;
    forTeamId?: uuid;
    //navItem: NavItem;
    navRoute: NavRoute;
    help: string;
    mode: FormMode;
    forceMode: FormMode;
    tabFields: ITabField[] = [];
    readonly = false;
    allowDelete = true;
    deleteMsg: string|null = null;
    deleteReasonNeeded = false;
    allowEdit = true;
    allowNew = true;
    objectFactory?: (queryParams: Params) => Observable<AbstractObject>;
    itemId?: uuid;
    itemTitle?: string;
    customTitle?: string;
    itemTitleGetter?: (o:AbstractObject) => string;
    actions?: IFormAction[] = [];
    newOptions?: NavOption[] = [];
    initNotification?: Subject<UntypedFormGroup>;
    /** Fired by form when it receives the configReady signal */
    isReady = new ReplaySubject<null>(1);
    forceTeamSwitchOnDrilldown? = false;
    hideFormActions? = false;
    fixedFormHeight? = true;
    /**
     * Override this to defer form initialization until you have finished prepping the configuation
     * Call configReady.next() on the object you have passed to signal that initialization may continue.
     * */
    configReady = new ReplaySubject<null>(1);
    charts: ChartAnalysis; // = new ChartAnalysis();
    /** Path to the corresponding Starlight documentation page, e.g. 'omc-community/managers/units'.
     *  Used by the test framework to validate savePageData() calls, and will power the in-app help icon. */
    docPath?: string;

    constructor(config: Partial<FormConfig>) {
        Object.getOwnPropertyNames(config).forEach((prop) => { this[prop] = config[prop]; });
        if (!config.configReady) {
            this.configReady.next(null); // Fire config ready, so when subscribed to it can go ahead and initialize
            this.configReady.complete();
        }
        if (config.isReady) { // Internal event, should not be accessed directly
            console.error('You should not pass isReady to a form, you probably meant to pass configReady');
        }
        this.configReady.subscribe(() => {
            this.isReady.next(null);
            this.isReady.complete();
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    beforeList(data: AbstractObject[], params: HttpParams) {
        return data;
    }

    beforeEdit(o): AbstractObject {
        return o;
    }

    pathEdit(o: AbstractObject) {
        //console.warn('Does it object to being prefixed with a slash');
        return this.navRoute.getIdUrl(o.id);
    }

    /*
    * Override to provide functionality to execute when parameters are set.
    */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    setTeam( teamId: uuid) {
    }

    tableHighlighter: (o: AbstractObject) => boolean;

    pathNew() {
        return this.navRoute.newUrl;
    }

    hasPersonName(item) {
        if (Object.prototype.hasOwnProperty.call(item, 'title')
        && Object.prototype.hasOwnProperty.call(item, 'lastName')
        && Object.prototype.hasOwnProperty.call(item, 'firstName')) {
            return true;
        }
        return false;
    }

    getPersonName(item) {
        let name = '';
        if (item.title) {
            name = item.title + ' ';
        }
        if (item.firstName) {
            name += item.firstName + ' '
        }
        if (item.lastName) {
            name += item.lastName;
        }
        return name;
    }

    setItemTitle(item: AbstractObject) {

        if (!item) {
            this.itemTitle = 'Not Found';
        } else if (this.itemTitleGetter) {
            this.itemTitle = this.itemTitleGetter(item);
        } else if (this.hasPersonName(item)) {
            this.itemTitle = this.getPersonName(item);
        } else if (Object.prototype.hasOwnProperty.call(item, 'title')) {
            this.itemTitle = item['title'];
            if (Object.prototype.hasOwnProperty.call(item, 'refNr')) {
                this.itemTitle += ' (' + item['refNr'] + ')';
            }
        } else if (Object.prototype.hasOwnProperty.call(item, 'name')) {
            this.itemTitle = item['name'];
            if ( Object.prototype.hasOwnProperty.call(item, 'refNr')) {
                this.itemTitle += ' (' + item['refNr'] + ')';
            }
        } else if (Object.prototype.hasOwnProperty.call(item, 'refNr')) {
            this.itemTitle = item['refNr'];
        //} else if (Object.prototype.hasOwnProperty.call(item, 'id')) {
        //    this.itemTitle = '' + item['id'];
        } else {
            this.itemTitle = '';
        }
        if (this.forTeamName) {
            this.itemTitle = this.itemTitle + ' (' + this.forTeamName + ')';
        }
    }

    getItemTitle() {
        if (this.mode === 'new') {
            return 'New';
        } else {
            return this.itemTitle;
        }
    }

    /** Pass alternative function to be executed before the save starts */
    beforeSave: (formGroup: UntypedFormGroup) => void;


    /** Pass an alternative function to be executed after the form has been saved and the response has been loaded */
    afterSave: (formGroup: UntypedFormGroup) => void;

    /*
    * Pass a function that returns a prompt for the user to confirm saving a record.
    * If the function returns null, save will continue without prompting user
    * If string is returned, user is prompted with the message to confirm save.
    */
    getConfirmSaveMessage: (formGroup: UntypedFormGroup) => string;

    getIcon() {
        return this.icon ?? this.navRoute?.icon;
    }

    getTitle() {
        let title = this.title;

        if (this.customTitle) {
            title = this.customTitle;
        } else if (this.mode === 'new') {
            title = 'New ' + this.title;
        } else if (this.mode === 'edit') {
            title = 'Edit ' + this.title;
        }
        if (this.forTeamName) {
            title = title + ' (' + this.forTeamName + ')';
        }

        return title;
    }

    /**
     * Fills the filter option lists from the rows on the table, so a column offers the values
     * actually present. Works off Field.getFilterOptions, so it covers both a PicklistField, which
     * shares one list with its editor, and a PickLinkField, which has a filter list only.
     */
    populatePicklists(o: AbstractObject[]) {
        const selfPoppers = this.fieldSet.fields.filter(f => f.getFilterOptions()?.selfPopulate);
        selfPoppers.forEach( sp => sp.getFilterOptions().items.length = 0)
        o.forEach(o => this.populatePLs(selfPoppers, o));
        selfPoppers.forEach ( f => {
            const list = f.getFilterOptions();
            list.items = list.items.sort((a,b) => a.name.localeCompare(b.name));
        })
    }

    private populatePLs(selfPoppers : Field[], o: AbstractObject) {
        selfPoppers.forEach ( f => {
            const id = f.getValue(o);
            if (id) {
                const list = f.getFilterOptions();
                const idx = list.items.find(i => i.id === id);
                if (!idx) {
                    const option: AbstractObject = list.makeOption
                        ? list.makeOption(o)
                        : { id, name: f.resolveValue(list['valueObjectName'], o) };
                    list.items.push({ ...option, id, count: 1 } as AbstractObject);
                } else {
                    idx['count'] += 1;
                }
            }
        });
    }
}
